
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../components/common/Card';
import { Zizo } from '../components/common/Zizo';
import { Page } from '../components/layout/Page';
import { useUserStore } from '../store/userStore';
import { LESSON_CONTENT } from '../data/lessonContent';

const TRUE_FALSE_CHALLENGES = [
  {
    id: 'tf-1',
    statement: 'منجم السكري من أشهر مناجم الذهب في مصر.',
    answer: true,
    explanation: 'صحيح، منجم السكري من أكبر مناجم الذهب.'
  },
  {
    id: 'tf-2',
    statement: 'الطاقة الشمسية مصدر غير متجدد.',
    answer: false,
    explanation: 'خطأ، الطاقة الشمسية مصدر متجدد.'
  },
  {
    id: 'tf-3',
    statement: 'الري بالتنقيط يساعد في توفير الماء.',
    answer: true,
    explanation: 'صحيح، لأنه يقلل الفاقد ويوصل الماء مباشرة للجذور.'
  },
  {
    id: 'tf-4',
    statement: 'النحاس ضعيف جدًا في توصيل الكهرباء.',
    answer: false,
    explanation: 'خطأ، النحاس من أفضل الموصلات الكهربائية.'
  },
  {
    id: 'tf-5',
    statement: 'القطن المصري معروف بجودته عالميًا.',
    answer: true,
    explanation: 'صحيح، القطن المصري مشهور عالميًا.'
  },
  {
    id: 'tf-6',
    statement: 'إغلاق الصنبور بعد الاستخدام يحافظ على الماء.',
    answer: true,
    explanation: 'صحيح، سلوك بسيط ومهم لتقليل الهدر.'
  },
  {
    id: 'tf-7',
    statement: 'كل الموارد الطبيعية تتجدد بسرعة.',
    answer: false,
    explanation: 'خطأ، بعض الموارد مثل المعادن غير متجددة.'
  },
  {
    id: 'tf-8',
    statement: 'الرياح يمكن استخدامها لإنتاج الكهرباء.',
    answer: true,
    explanation: 'صحيح، طاقة الرياح من مصادر الطاقة المتجددة.'
  },
  {
    id: 'tf-9',
    statement: 'المياه الجوفية موجودة فوق سطح الأرض.',
    answer: false,
    explanation: 'خطأ، المياه الجوفية تكون تحت سطح الأرض.'
  },
  {
    id: 'tf-10',
    statement: 'الفوسفات يدخل في صناعة الأسمدة.',
    answer: true,
    explanation: 'صحيح، الفوسفات عنصر أساسي في الأسمدة.'
  }
];

const FILL_BLANK_CHALLENGES = [
  {
    id: 'fill-1',
    sentence: 'نهر ____ هو شريان الحياة في مصر.',
    accepted: ['النيل']
  },
  {
    id: 'fill-2',
    sentence: 'من أشهر مناجم الذهب في مصر منجم ____.',
    accepted: ['السكري']
  },
  {
    id: 'fill-3',
    sentence: 'الطاقة ____ من المصادر المتجددة النظيفة.',
    accepted: ['الشمسية']
  },
  {
    id: 'fill-4',
    sentence: 'الري ____ يساعد على تقليل هدر المياه.',
    accepted: ['بالتنقيط', 'تنقيط']
  },
  {
    id: 'fill-5',
    sentence: 'المعادن موارد ____ تحتاج زمن طويل لتتكون.',
    accepted: ['غير متجددة', 'غيرمتجددة']
  },
  {
    id: 'fill-6',
    sentence: 'يدخل ____ في صناعة الأسمدة الزراعية.',
    accepted: ['الفوسفات']
  },
  {
    id: 'fill-7',
    sentence: 'إعادة ____ تقلل التلوث وتحافظ على الموارد.',
    accepted: ['التدوير']
  },
  {
    id: 'fill-8',
    sentence: 'النحاس معدن ممتاز لتوصيل ____.',
    accepted: ['الكهرباء']
  }
];

const ORDERING_CHALLENGES = [
  {
    id: 'order-1',
    title: 'رتب مراحل استخراج المعدن',
    steps: ['استكشاف الموقع', 'استخراج الخام', 'تنقية الخام', 'استخدام المعدن في الصناعة']
  },
  {
    id: 'order-2',
    title: 'رتب خطوات ترشيد الماء',
    steps: ['ملاحظة الهدر', 'إصلاح التسريب', 'استخدام أدوات موفرة', 'متابعة الاستهلاك']
  },
  {
    id: 'order-3',
    title: 'رتب مسار التعلم',
    steps: ['قراءة الدرس', 'حل الأسئلة', 'تنفيذ نشاط عملي', 'مراجعة الأخطاء']
  },
  {
    id: 'order-4',
    title: 'رتب توليد الطاقة الشمسية',
    steps: ['سقوط الضوء', 'تحويل الضوء لكهرباء', 'نقل الكهرباء', 'استخدامها في المنزل']
  }
];

const ODD_ONE_OUT_CHALLENGES = [
  {
    id: 'odd-1',
    question: 'اختر المختلف:',
    options: ['الذهب', 'النحاس', 'الفوسفات', 'المطر'],
    answer: 3,
    reason: 'المطر ليس معدنًا.'
  },
  {
    id: 'odd-2',
    question: 'اختر المختلف:',
    options: ['الرياح', 'الشمس', 'الفحم', 'المياه'],
    answer: 2,
    reason: 'الفحم ليس مصدرًا متجددًا.'
  },
  {
    id: 'odd-3',
    question: 'اختر المختلف:',
    options: ['القمح', 'الأرز', 'القطن', 'الحديد'],
    answer: 3,
    reason: 'الحديد معدن وليس محصولًا.'
  },
  {
    id: 'odd-4',
    question: 'اختر المختلف:',
    options: ['دلتا', 'نهر', 'واحة', 'حاسوب'],
    answer: 3,
    reason: 'الحاسوب ليس عنصرًا جغرافيًا.'
  },
  {
    id: 'odd-5',
    question: 'اختر المختلف:',
    options: ['إغلاق الصنبور', 'إعادة التدوير', 'رمي القمامة في النيل', 'الري بالتنقيط'],
    answer: 2,
    reason: 'هذا سلوك ضار بالبيئة.'
  },
  {
    id: 'odd-6',
    question: 'اختر المختلف:',
    options: ['سيناء', 'الصحراء الشرقية', 'دلتا النيل', 'كوكب المريخ'],
    answer: 3,
    reason: 'المريخ ليس مكانًا في مصر.'
  }
];

const MEMORY_SYMBOLS = ['🪨', '⚡', '🌾', '💧', '🗺️', '🌞', '🧪', '🚜'];

const shuffle = (items) => {
  const cloned = [...items];
  for (let i = cloned.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
};

const normalizeAnswer = (value = '') =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[ًٌٍَُِّْـ]/g, '')
    .replace(/[اأإآ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/\s+/g, ' ');

const buildMcqBank = () =>
  Object.entries(LESSON_CONTENT).flatMap(([lessonId, lesson]) =>
    (lesson?.questions?.mcq || []).map((question, index) => ({
      id: `${lessonId}-mcq-${index}`,
      question
    }))
  );

const buildGlossaryBank = () =>
  Object.entries(LESSON_CONTENT).flatMap(([lessonId, lesson]) =>
    (lesson?.glossary || []).map((item, index) => ({
      id: `${lessonId}-glossary-${index}`,
      term: item.term,
      definition: item.definition
    }))
  );

const createMemoryDeck = () =>
  shuffle(
    MEMORY_SYMBOLS.flatMap((symbol, index) => [
      { id: `${index}-a`, pairId: symbol, symbol },
      { id: `${index}-b`, pairId: symbol, symbol }
    ])
  );

const randomTapPosition = () => ({
  x: Math.floor(Math.random() * 70) + 10,
  y: Math.floor(Math.random() * 60) + 8
});

export const Games = () => {
  const { points, level, gamesWon, addPoints, incrementGamesWon } = useUserStore();

  const mcqBank = useMemo(() => buildMcqBank(), []);
  const glossaryBank = useMemo(() => buildGlossaryBank(), []);

  const [mcqRound, setMcqRound] = useState(0);
  const [tfRound, setTfRound] = useState(0);
  const [fillRound, setFillRound] = useState(0);
  const [matchingRound, setMatchingRound] = useState(0);
  const [oddRound, setOddRound] = useState(0);

  const mcqQuestions = useMemo(() => shuffle(mcqBank).slice(0, 8), [mcqBank, mcqRound]);
  const tfQuestions = useMemo(() => shuffle(TRUE_FALSE_CHALLENGES).slice(0, 8), [tfRound]);
  const fillQuestions = useMemo(() => shuffle(FILL_BLANK_CHALLENGES).slice(0, 8), [fillRound]);
  const oddQuestions = useMemo(() => shuffle(ODD_ONE_OUT_CHALLENGES).slice(0, 6), [oddRound]);

  const matchingPairs = useMemo(
    () => shuffle(glossaryBank).slice(0, Math.min(6, glossaryBank.length)),
    [glossaryBank, matchingRound]
  );
  const matchingDefinitions = useMemo(
    () => shuffle(matchingPairs.map((item) => ({ id: item.id, definition: item.definition }))),
    [matchingPairs]
  );

  const [mcqAnswers, setMcqAnswers] = useState({});
  const [tfAnswers, setTfAnswers] = useState({});
  const [fillInputs, setFillInputs] = useState({});
  const [fillResults, setFillResults] = useState({});
  const [matchingAnswers, setMatchingAnswers] = useState({});
  const [oddAnswers, setOddAnswers] = useState({});

  const [mcqRewardClaimed, setMcqRewardClaimed] = useState(false);
  const [tfRewardClaimed, setTfRewardClaimed] = useState(false);
  const [fillRewardClaimed, setFillRewardClaimed] = useState(false);
  const [matchingRewardClaimed, setMatchingRewardClaimed] = useState(false);
  const [oddRewardClaimed, setOddRewardClaimed] = useState(false);

  const [memoryDeck, setMemoryDeck] = useState(() => createMemoryDeck());
  const [memoryFlipped, setMemoryFlipped] = useState([]);
  const [memoryMatched, setMemoryMatched] = useState([]);
  const [memoryMoves, setMemoryMoves] = useState(0);
  const [memoryRewardClaimed, setMemoryRewardClaimed] = useState(false);

  const [tapRunning, setTapRunning] = useState(false);
  const [tapTimeLeft, setTapTimeLeft] = useState(20);
  const [tapScore, setTapScore] = useState(0);
  const [tapBest, setTapBest] = useState(0);
  const [tapPosition, setTapPosition] = useState({ x: 40, y: 40 });
  const [tapFinished, setTapFinished] = useState(false);
  const [tapRewardClaimed, setTapRewardClaimed] = useState(false);

  const [orderRound, setOrderRound] = useState(0);
  const [orderItems, setOrderItems] = useState([]);
  const [orderRewardClaimed, setOrderRewardClaimed] = useState(false);

  const currentOrderChallenge = useMemo(
    () => ORDERING_CHALLENGES[orderRound % ORDERING_CHALLENGES.length],
    [orderRound]
  );

  useEffect(() => {
    const prepared = currentOrderChallenge.steps.map((step, index) => ({
      id: `${currentOrderChallenge.id}-${index}`,
      text: step,
      correctIndex: index
    }));
    setOrderItems(shuffle(prepared));
    setOrderRewardClaimed(false);
  }, [currentOrderChallenge]);

  const mcqAnsweredCount = Object.keys(mcqAnswers).length;
  const mcqScore = mcqQuestions.reduce((score, item) => {
    return mcqAnswers[item.id] === item.question.answer ? score + 1 : score;
  }, 0);

  const tfAnsweredCount = Object.keys(tfAnswers).length;
  const tfScore = tfQuestions.reduce((score, item) => {
    return tfAnswers[item.id] === item.answer ? score + 1 : score;
  }, 0);

  const fillCheckedCount = Object.keys(fillResults).length;
  const fillScore = fillQuestions.reduce((score, item) => {
    return fillResults[item.id] ? score + 1 : score;
  }, 0);

  const matchingAnsweredCount = Object.keys(matchingAnswers).length;
  const matchingScore = matchingPairs.reduce((score, item) => {
    return matchingAnswers[item.id] === item.id ? score + 1 : score;
  }, 0);

  const oddAnsweredCount = Object.keys(oddAnswers).length;
  const oddScore = oddQuestions.reduce((score, item) => {
    return oddAnswers[item.id] === item.answer ? score + 1 : score;
  }, 0);

  const memoryCompleted = memoryMatched.length === MEMORY_SYMBOLS.length;
  const orderSolved =
    orderItems.length > 0 && orderItems.every((item, index) => item.correctIndex === index);
  const tapSuccess = tapFinished && tapScore >= 12;

  useEffect(() => {
    if (memoryFlipped.length !== 2) {
      return undefined;
    }

    const [firstIndex, secondIndex] = memoryFlipped;
    const firstCard = memoryDeck[firstIndex];
    const secondCard = memoryDeck[secondIndex];

    setMemoryMoves((prev) => prev + 1);

    if (firstCard.pairId === secondCard.pairId) {
      setMemoryMatched((prev) =>
        prev.includes(firstCard.pairId) ? prev : [...prev, firstCard.pairId]
      );
      setMemoryFlipped([]);
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setMemoryFlipped([]);
    }, 650);

    return () => clearTimeout(timeoutId);
  }, [memoryFlipped, memoryDeck]);

  useEffect(() => {
    if (!tapRunning) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setTapTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalId);
          setTapRunning(false);
          setTapFinished(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [tapRunning]);

  useEffect(() => {
    if (!tapFinished) return;
    setTapBest((prev) => Math.max(prev, tapScore));
  }, [tapFinished, tapScore]);

  const resetMcqRound = () => {
    setMcqRound((prev) => prev + 1);
    setMcqAnswers({});
    setMcqRewardClaimed(false);
  };

  const resetTfRound = () => {
    setTfRound((prev) => prev + 1);
    setTfAnswers({});
    setTfRewardClaimed(false);
  };

  const resetFillRound = () => {
    setFillRound((prev) => prev + 1);
    setFillInputs({});
    setFillResults({});
    setFillRewardClaimed(false);
  };

  const resetMatchingRound = () => {
    setMatchingRound((prev) => prev + 1);
    setMatchingAnswers({});
    setMatchingRewardClaimed(false);
  };

  const resetOddRound = () => {
    setOddRound((prev) => prev + 1);
    setOddAnswers({});
    setOddRewardClaimed(false);
  };

  const handleFillCheck = (question) => {
    const inputValue = normalizeAnswer(fillInputs[question.id] || '');
    const isCorrect = question.accepted.some(
      (acceptedValue) => normalizeAnswer(acceptedValue) === inputValue
    );

    setFillResults((prev) => ({
      ...prev,
      [question.id]: isCorrect
    }));
  };

  const resetMemoryGame = () => {
    setMemoryDeck(createMemoryDeck());
    setMemoryFlipped([]);
    setMemoryMatched([]);
    setMemoryMoves(0);
    setMemoryRewardClaimed(false);
  };

  const handleMemoryClick = (index) => {
    if (memoryFlipped.length >= 2) return;
    const card = memoryDeck[index];
    if (!card) return;
    if (memoryFlipped.includes(index) || memoryMatched.includes(card.pairId)) return;
    setMemoryFlipped((prev) => [...prev, index]);
  };

  const startTapGame = () => {
    setTapRunning(true);
    setTapTimeLeft(20);
    setTapScore(0);
    setTapFinished(false);
    setTapRewardClaimed(false);
    setTapPosition(randomTapPosition());
  };

  const handleTapTargetClick = () => {
    if (!tapRunning) return;
    setTapScore((prev) => prev + 1);
    setTapPosition(randomTapPosition());
  };

  const moveOrderItem = (index, direction) => {
    setOrderItems((prev) => {
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;
      const cloned = [...prev];
      [cloned[index], cloned[targetIndex]] = [cloned[targetIndex], cloned[index]];
      return cloned;
    });
  };

  return (
    <Page className="space-y-8">
      <section className="glass-panel p-7 md:p-10 relative overflow-hidden">
        <div className="absolute -top-28 -right-24 w-72 h-72 rounded-full bg-cyan-300/35 blur-3xl" />
        <div className="absolute -bottom-28 -left-24 w-80 h-80 rounded-full bg-pink-300/28 blur-3xl" />
        <div className="absolute top-6 left-8 w-4 h-4 rounded-full bg-yellow-300/90" style={{ animation: 'floatOrb 8s ease-in-out infinite' }} />
        <div className="absolute bottom-10 right-16 w-3 h-3 rounded-full bg-emerald-300/90" style={{ animation: 'floatOrb 9.3s ease-in-out infinite reverse' }} />

        <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 items-center">
          <div className="space-y-3">
            <h1 className="text-4xl md:text-5xl font-black text-gray-900">مدينة الألعاب التعليمية</h1>
            <p className="text-gray-700 max-w-2xl">
              الصفحة الآن مليانة ألعاب مختلفة للأطفال: سرعة، ذاكرة، ترتيب، توصيل، أسئلة سريعة وألغاز ممتعة.
              العب أكتر واجمع نقاط أكتر.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="chip chip-strong">8 ألعاب متنوعة</span>
              <span className="chip">ذاكرة</span>
              <span className="chip">سرعة</span>
              <span className="chip">ترتيب</span>
              <span className="chip">توصيل</span>
              <span className="chip">ألغاز</span>
            </div>
          </div>
          <div className="justify-self-center">
            <Zizo size="xlarge" expression="excited" />
          </div>
        </div>
      </section>

      <Card className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-2xl font-black text-gray-900">لوحة إنجاز الألعاب</h2>
          <span className="chip chip-strong">كل لعبة لها مكافأة</span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
          <div className="map-soft-box">
            <p className="number font-black text-gray-900 text-xl">{points}</p>
            <p className="text-xs text-gray-600">النقاط</p>
          </div>
          <div className="map-soft-box">
            <p className="number font-black text-gray-900 text-xl">{gamesWon}</p>
            <p className="text-xs text-gray-600">ألعاب مكتملة</p>
          </div>
          <div className="map-soft-box">
            <p className="number font-black text-gray-900 text-xl">{level}</p>
            <p className="text-xs text-gray-600">المستوى</p>
          </div>
          <div className="map-soft-box">
            <p className="number font-black text-gray-900 text-xl">8</p>
            <p className="text-xs text-gray-600">أشكال ألعاب</p>
          </div>
        </div>
      </Card>

      <section className="space-y-4">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Card className="space-y-4 relative overflow-hidden">
            <div className="absolute -top-12 -right-8 w-32 h-32 rounded-full bg-sky-200/40 blur-2xl" />
            <div className="relative flex items-center justify-between gap-2">
              <h3 className="text-xl font-black text-gray-900">لعبة الاختيار من متعدد</h3>
              <span className="chip">النتيجة {mcqScore}/{mcqQuestions.length}</span>
            </div>

            <div className="space-y-3 max-h-[410px] overflow-y-auto pr-1">
              {mcqQuestions.map((item, index) => {
                const selected = mcqAnswers[item.id];
                const answered = selected !== undefined;
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.02 }}
                    className="rounded-2xl border border-white/40 bg-white/70 p-3 space-y-2"
                  >
                    <p className="font-semibold text-gray-900">{index + 1}. {item.question.q}</p>
                    <div className="grid grid-cols-1 gap-2">
                      {item.question.options.map((option, optionIndex) => {
                        const isCorrect = optionIndex === item.question.answer;
                        const isSelected = selected === optionIndex;
                        const styleClass = answered
                          ? isCorrect
                            ? 'border-palmGreen bg-palmGreen/10 text-palmGreen'
                            : isSelected
                              ? 'border-red-300 bg-red-50 text-red-700'
                              : 'border-gray-200 bg-white'
                          : 'border-gray-200 bg-white hover:border-cyan-300';

                        return (
                          <button
                            key={`${item.id}-${option}`}
                            type="button"
                            disabled={answered}
                            onClick={() => setMcqAnswers((prev) => ({ ...prev, [item.id]: optionIndex }))}
                            className={`text-right rounded-xl border px-3 py-2 text-sm transition ${styleClass}`}
                          >
                            {option}
                          </button>
                        );
                      })}
                    </div>
                    {answered && <p className="text-xs text-gray-600">{item.question.explanation}</p>}
                  </motion.div>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" className="chip" onClick={resetMcqRound}>أسئلة جديدة</button>
              {mcqAnsweredCount === mcqQuestions.length && !mcqRewardClaimed && (
                <button
                  type="button"
                  className="chip chip-strong"
                  onClick={() => {
                    addPoints(mcqScore >= 6 ? 20 : 10);
                    incrementGamesWon();
                    setMcqRewardClaimed(true);
                  }}
                >
                  استلام المكافأة
                </button>
              )}
            </div>
          </Card>

          <Card className="space-y-4 relative overflow-hidden">
            <div className="absolute -top-12 -left-8 w-32 h-32 rounded-full bg-pink-200/40 blur-2xl" />
            <div className="relative flex items-center justify-between gap-2">
              <h3 className="text-xl font-black text-gray-900">لعبة صح أم خطأ</h3>
              <span className="chip">النتيجة {tfScore}/{tfQuestions.length}</span>
            </div>

            <div className="space-y-3 max-h-[410px] overflow-y-auto pr-1">
              {tfQuestions.map((item, index) => {
                const selected = tfAnswers[item.id];
                const answered = selected !== undefined;
                return (
                  <div key={item.id} className="rounded-2xl border border-white/40 bg-white/70 p-3 space-y-2">
                    <p className="font-semibold text-gray-900">{index + 1}. {item.statement}</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={answered}
                        onClick={() => setTfAnswers((prev) => ({ ...prev, [item.id]: true }))}
                        className={`chip ${answered && selected === true && item.answer ? 'chip-strong' : ''}`}
                      >
                        صح
                      </button>
                      <button
                        type="button"
                        disabled={answered}
                        onClick={() => setTfAnswers((prev) => ({ ...prev, [item.id]: false }))}
                        className={`chip ${answered && selected === false && !item.answer ? 'chip-strong' : ''}`}
                      >
                        خطأ
                      </button>
                    </div>
                    {answered && <p className="text-xs text-gray-600">{item.explanation}</p>}
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" className="chip" onClick={resetTfRound}>تحدي جديد</button>
              {tfAnsweredCount === tfQuestions.length && !tfRewardClaimed && (
                <button
                  type="button"
                  className="chip chip-strong"
                  onClick={() => {
                    addPoints(tfScore >= 6 ? 16 : 8);
                    incrementGamesWon();
                    setTfRewardClaimed(true);
                  }}
                >
                  استلام المكافأة
                </button>
              )}
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Card className="space-y-4 relative overflow-hidden">
            <div className="absolute -bottom-12 -right-10 w-32 h-32 rounded-full bg-emerald-200/40 blur-2xl" />
            <div className="relative flex items-center justify-between gap-2">
              <h3 className="text-xl font-black text-gray-900">لعبة أكمل الفراغ</h3>
              <span className="chip">النتيجة {fillScore}/{fillQuestions.length}</span>
            </div>

            <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
              {fillQuestions.map((item, index) => {
                const result = fillResults[item.id];
                return (
                  <div key={item.id} className="rounded-2xl border border-white/40 bg-white/70 p-3 space-y-2">
                    <p className="font-semibold text-gray-900">{index + 1}. {item.sentence}</p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={fillInputs[item.id] || ''}
                        onChange={(event) =>
                          setFillInputs((prev) => ({ ...prev, [item.id]: event.target.value }))
                        }
                        className="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm"
                        placeholder="اكتب الإجابة"
                      />
                      <button type="button" className="chip" onClick={() => handleFillCheck(item)}>
                        تحقق
                      </button>
                    </div>
                    {result !== undefined && (
                      <p className={`text-xs ${result ? 'text-palmGreen' : 'text-red-600'}`}>
                        {result ? 'إجابة صحيحة 👏' : `الإجابة الأفضل: ${item.accepted[0]}`}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" className="chip" onClick={resetFillRound}>مجموعة جديدة</button>
              {fillCheckedCount === fillQuestions.length && !fillRewardClaimed && (
                <button
                  type="button"
                  className="chip chip-strong"
                  onClick={() => {
                    addPoints(fillScore >= 6 ? 16 : 9);
                    incrementGamesWon();
                    setFillRewardClaimed(true);
                  }}
                >
                  استلام المكافأة
                </button>
              )}
            </div>
          </Card>

          <Card className="space-y-4 relative overflow-hidden">
            <div className="absolute -bottom-12 -left-10 w-32 h-32 rounded-full bg-violet-200/40 blur-2xl" />
            <div className="relative flex items-center justify-between gap-2">
              <h3 className="text-xl font-black text-gray-900">لعبة التوصيل</h3>
              <span className="chip">الصحيح {matchingScore}/{matchingPairs.length}</span>
            </div>

            <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
              {matchingPairs.map((item, index) => (
                <div key={item.id} className="rounded-2xl border border-white/40 bg-white/70 p-3 space-y-2">
                  <p className="font-semibold text-gray-900">{index + 1}. {item.term}</p>
                  <select
                    value={matchingAnswers[item.id] || ''}
                    onChange={(event) =>
                      setMatchingAnswers((prev) => ({ ...prev, [item.id]: event.target.value }))
                    }
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                  >
                    <option value="">اختر التعريف المناسب</option>
                    {matchingDefinitions.map((definition) => (
                      <option key={`${item.id}-${definition.id}`} value={definition.id}>
                        {definition.definition}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" className="chip" onClick={resetMatchingRound}>توصيل جديد</button>
              {matchingAnsweredCount === matchingPairs.length && !matchingRewardClaimed && (
                <button
                  type="button"
                  className="chip chip-strong"
                  onClick={() => {
                    addPoints(matchingScore >= 5 ? 18 : 10);
                    incrementGamesWon();
                    setMatchingRewardClaimed(true);
                  }}
                >
                  استلام المكافأة
                </button>
              )}
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Card className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-black text-gray-900">لعبة ترتيب الخطوات</h3>
              <span className="chip">{currentOrderChallenge.title}</span>
            </div>

            <div className="space-y-2">
              {orderItems.map((item, index) => (
                <div
                  key={item.id}
                  className={`rounded-xl border p-2 flex items-center gap-2 ${
                    orderSolved ? 'border-palmGreen bg-palmGreen/10' : 'border-white/40 bg-white/70'
                  }`}
                >
                  <span className="chip number">{index + 1}</span>
                  <p className="text-sm text-gray-800 flex-1">{item.text}</p>
                  <div className="flex gap-1">
                    <button type="button" className="chip" onClick={() => moveOrderItem(index, 'up')}>⬆️</button>
                    <button type="button" className="chip" onClick={() => moveOrderItem(index, 'down')}>⬇️</button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" className="chip" onClick={() => setOrderRound((prev) => prev + 1)}>
                ترتيب جديد
              </button>
              {orderSolved && !orderRewardClaimed && (
                <button
                  type="button"
                  className="chip chip-strong"
                  onClick={() => {
                    addPoints(18);
                    incrementGamesWon();
                    setOrderRewardClaimed(true);
                  }}
                >
                  استلام المكافأة
                </button>
              )}
            </div>
          </Card>

          <Card className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-black text-gray-900">اكتشف المختلف</h3>
              <span className="chip">النتيجة {oddScore}/{oddQuestions.length}</span>
            </div>

            <div className="space-y-3 max-h-[410px] overflow-y-auto pr-1">
              {oddQuestions.map((item, index) => {
                const selected = oddAnswers[item.id];
                const answered = selected !== undefined;

                return (
                  <div key={item.id} className="rounded-2xl border border-white/40 bg-white/70 p-3 space-y-2">
                    <p className="font-semibold text-gray-900">{index + 1}. {item.question}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {item.options.map((option, optionIndex) => {
                        const isCorrect = optionIndex === item.answer;
                        const isSelected = selected === optionIndex;

                        const styleClass = answered
                          ? isCorrect
                            ? 'border-palmGreen bg-palmGreen/10 text-palmGreen'
                            : isSelected
                              ? 'border-red-300 bg-red-50 text-red-700'
                              : 'border-gray-200 bg-white'
                          : 'border-gray-200 bg-white hover:border-cyan-300';

                        return (
                          <button
                            key={`${item.id}-${option}`}
                            type="button"
                            disabled={answered}
                            onClick={() => setOddAnswers((prev) => ({ ...prev, [item.id]: optionIndex }))}
                            className={`text-right rounded-xl border px-3 py-2 text-sm transition ${styleClass}`}
                          >
                            {option}
                          </button>
                        );
                      })}
                    </div>
                    {answered && <p className="text-xs text-gray-600">{item.reason}</p>}
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" className="chip" onClick={resetOddRound}>ألغاز جديدة</button>
              {oddAnsweredCount === oddQuestions.length && !oddRewardClaimed && (
                <button
                  type="button"
                  className="chip chip-strong"
                  onClick={() => {
                    addPoints(oddScore >= 4 ? 14 : 8);
                    incrementGamesWon();
                    setOddRewardClaimed(true);
                  }}
                >
                  استلام المكافأة
                </button>
              )}
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Card className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-black text-gray-900">لعبة الذاكرة</h3>
              <span className="chip">حركات {memoryMoves}</span>
            </div>

            <div className="grid grid-cols-4 gap-2">
              {memoryDeck.map((card, index) => {
                const isFlipped =
                  memoryFlipped.includes(index) || memoryMatched.includes(card.pairId);
                return (
                  <button
                    key={card.id}
                    type="button"
                    onClick={() => handleMemoryClick(index)}
                    className={`h-14 rounded-xl border text-2xl transition ${
                      isFlipped
                        ? 'border-gold bg-gold/20'
                        : 'border-gray-200 bg-white hover:border-cyan-300'
                    }`}
                  >
                    {isFlipped ? card.symbol : '❓'}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" className="chip" onClick={resetMemoryGame}>إعادة اللعبة</button>
              {memoryCompleted && !memoryRewardClaimed && (
                <button
                  type="button"
                  className="chip chip-strong"
                  onClick={() => {
                    addPoints(memoryMoves <= 12 ? 24 : 14);
                    incrementGamesWon();
                    setMemoryRewardClaimed(true);
                  }}
                >
                  استلام المكافأة
                </button>
              )}
            </div>
          </Card>

          <Card className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-black text-gray-900">لعبة السرعة</h3>
              <span className="chip">أفضل نتيجة {tapBest}</span>
            </div>

            <div className="rounded-2xl border border-white/40 bg-white/70 p-3 space-y-3">
              <div className="flex items-center justify-between text-sm text-gray-700">
                <span>الوقت: {tapTimeLeft} ثانية</span>
                <span>النقاط: {tapScore}</span>
              </div>

              <div className="relative h-52 rounded-2xl bg-gradient-to-b from-sky-100 to-yellow-100 overflow-hidden border border-white/60">
                <motion.button
                  type="button"
                  onClick={handleTapTargetClick}
                  disabled={!tapRunning}
                  whileTap={{ scale: 0.92 }}
                  className="absolute text-3xl w-14 h-14 rounded-full bg-white/90 border border-gold shadow-sm flex items-center justify-center disabled:opacity-50"
                  style={{
                    left: `${tapPosition.x}%`,
                    top: `${tapPosition.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  ⭐
                </motion.button>
              </div>

              <div className="flex flex-wrap gap-2">
                {!tapRunning ? (
                  <button type="button" className="chip" onClick={startTapGame}>ابدأ 20 ثانية</button>
                ) : (
                  <span className="chip chip-strong">اضغط النجمة بسرعة</span>
                )}

                {tapSuccess && !tapRewardClaimed && (
                  <button
                    type="button"
                    className="chip chip-strong"
                    onClick={() => {
                      addPoints(tapScore >= 18 ? 24 : 14);
                      incrementGamesWon();
                      setTapRewardClaimed(true);
                    }}
                  >
                    استلام المكافأة
                  </button>
                )}
              </div>

              {tapFinished && (
                <p className={`text-xs font-semibold ${tapSuccess ? 'text-palmGreen' : 'text-gray-600'}`}>
                  {tapSuccess
                    ? 'ممتاز! حققت نتيجة قوية في لعبة السرعة.'
                    : 'نتيجة جيدة. حاول مرة ثانية ووصل إلى 12 نقطة.'}
                </p>
              )}
            </div>
          </Card>
        </div>
      </section>
    </Page>
  );
};
